import { DOCUMENT } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { GenericError } from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthHelpComponent } from '../components/auth-help/auth-help.component';
import { AuthPanelComponent } from '../components/auth-panel/auth-panel.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';

type AuthErrorKind = 'account_disabled' | 'sign_in_interrupted' | 'generic';

@Component({
  selector: 'app-error-page',
  templateUrl: './error.component.html',
  imports: [
    TranslocoModule,
    AuthHeaderComponent,
    AuthHelpComponent,
    AuthPanelComponent,
    AuthShellComponent,
    VxButtonComponent,
    VxSkeletonComponent,
  ],
})
export class ErrorPageComponent implements OnInit {
  private auth = inject(KratosAuthenticationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private document = inject(DOCUMENT);

  /** Undefined until the flow error is loaded — the page shows a skeleton. */
  public errorKind = signal<AuthErrorKind | undefined>(undefined);

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['id']) {
        this.loadError(params['id']);
      } else {
        this.errorKind.set('generic');
      }
    });
  }

  private async loadError(id: string) {
    try {
      const error = await this.auth.getFlowError(id);
      this.errorKind.set(this.classifyError(error));
    } catch {
      this.errorKind.set('generic');
    }
  }

  private classifyError(error: GenericError): AuthErrorKind {
    if (error.message === 'identity is disabled') {
      return 'account_disabled';
    }

    // The OIDC callback no longer matches the browser's continuity cookie —
    // a second social sign-in started before the first returned, or the
    // cookie expired, was blocked or cleared. Starting over fixes it.
    if (
      error.message === 'no resumable session found' ||
      error.reason?.includes('state parameter does not match')
    ) {
      return 'sign_in_interrupted';
    }

    return 'generic';
  }

  // The flow error doesn't say whether it was a login or a registration.
  // Login serves both: Kratos turns a social login for an unknown identity
  // into a registration, and the page links to password sign-up.
  public restartSignIn() {
    this.router.navigate(['/login']);
  }

  public openContactDetails() {
    this.document.defaultView?.open('https://incidite.com/contact', '_blank');
  }
}
